import { escapeHtmlTags } from '@chiroup/core/functions/stripHtmlTags';
import { Muscle } from '@chiroup/core/types/Muscle.type';
import { ImprovementOptionsText, Visit } from '@chiroup/core/types/PatientVisit.type';
import { CustomizationListItem } from '../../../../clinical-skills/CustomizationListItem.type';
import {
  getJointContent,
  getMyofascialContent,
  getNeurologicalContent,
  getOrthopedicContent,
  getROMContent,
} from './objectiveStrBuilder';

// Helper function to check if a value is a side object with left/right properties
const isSideObject = (value: any): value is { left?: boolean; right?: boolean } => {
  return typeof value === 'object' && value !== null && ('left' in value || 'right' in value);
};

// Sort V2 unified joint data by side: bilateral/both first, then left, then right
const sortUnifiedJointsByBilateralLeftRight = (jointsData: any) => {
  if (!jointsData?.detail) return jointsData;

  const sortedDetail: any = {};

  Object.keys(jointsData.detail).forEach(region => {
    const regionData = jointsData.detail[region];
    if (!regionData) return;

    if (region !== 'SPINE') {
      const entries = Object.entries(regionData);
      const jointEntries = entries.filter(
        ([key]) => key !== 'complaintIds' && key !== 'addedManually'
      );
      const metadataEntries = entries.filter(
        ([key]) => key === 'complaintIds' || key === 'addedManually'
      );

      if (jointEntries.length === 0) {
        return;
      }

      jointEntries.sort(([, valueA], [, valueB]) => {
        if (isSideObject(valueA) && isSideObject(valueB)) {
          const getSideOrder = (value: { left?: boolean; right?: boolean }) => {
            if (value.left && value.right) return 1;
            if (value.left) return 2;
            if (value.right) return 3;

            return 4;
          };

          return getSideOrder(valueA) - getSideOrder(valueB);
        }

        return 0;
      });

      sortedDetail[region] = {};
      jointEntries.forEach(([key, value]) => {
        sortedDetail[region][key] = value;
      });
      metadataEntries.forEach(([key, value]) => {
        sortedDetail[region][key] = value;
      });
    } else {
      const entries = Object.entries(regionData);
      const spineEntries = entries.filter(
        ([key]) => key !== 'complaintIds' && key !== 'addedManually'
      );

      if (spineEntries.length > 0) {
        sortedDetail[region] = {};
        spineEntries.forEach(([key, value]) => {
          sortedDetail[region][key] = value;
        });
      }
    }
  });

  return {
    ...jointsData,
    detail: sortedDetail,
  };
};

export const getUnifiedObjectiveContentFn = (
  visitForm: Visit,
  muscles: { data: Muscle[] },
  evals: { data: CustomizationListItem[] }
): string => {
  const unifiedObjective = visitForm.unifiedObjective;

  if (!unifiedObjective) {
    return '';
  }

  const hasObjectiveData = Object.keys(unifiedObjective).filter(
    key => key !== 'carryForward' && key !== 'activeComplaintRegions'
  ).length;

  if (!hasObjectiveData) {
    return '';
  }

  // V1 logic: Skip if only Orthopedic exists but has no actual detail data
  const hasObjectiveOrthopedicDetail = Object.values(
    unifiedObjective?.Orthopedic?.detail || {}
  ).some((det: any) => !!det?.value);
  const hasOnlyOrthopedic = !!(hasObjectiveData === 1 && unifiedObjective?.Orthopedic);

  if (hasOnlyOrthopedic && !hasObjectiveOrthopedicDetail) {
    return '';
  }

  let content = '';

  const shouldShowSection = (sectionData: any): boolean => {
    if (!sectionData) return false;

    return (
      !!(sectionData.detail && Object.keys(sectionData.detail).length > 0) || !!sectionData.value
    );
  };

  // Range of Motion Section (V2): only include in SOAP when a region was touched this visit
  if (shouldShowSection(unifiedObjective.RangeOfMotion)) {
    const value = unifiedObjective.RangeOfMotion?.value;
    const romDataWithTouched = unifiedObjective.RangeOfMotion;
    const hasTouchedRegions = Object.values(romDataWithTouched?.detail || {}).some(
      (regionData: any) => regionData?.touched === true
    );

    // Get last visit ROM data for unified comparison function
    const lastVisitROM = visitForm.lastVisitUnifiedObjective?.RangeOfMotion || {};
    const romContent =
      hasTouchedRegions || value ? getROMContent(romDataWithTouched || {}, lastVisitROM) : '';

    // A touched region with every value cleared produces no content — don't emit a bare header.
    if (value || romContent) {
      const header = value
        ? `<p><span style="font-weight: bold">Range of motion</span> is ${ImprovementOptionsText[value]} as compared to the preceding visit. `
        : `<h3>Range of Motion</h3>`;

      content += header;
      content += romContent;
      content += '</p><br/>';
    }
  }

  // Joints Section
  if (shouldShowSection(unifiedObjective.Joints)) {
    const detail = unifiedObjective.Joints?.detail || {};
    const value = unifiedObjective.Joints?.value;

    // For joints, check if there's actual data in the detail (not touched property)
    const hasJointData =
      Object.keys(detail).length > 0 &&
      Object.values(detail).some(
        (regionData: any) =>
          regionData && typeof regionData === 'object' && Object.keys(regionData).length > 0
      );

    if (hasJointData || value) {
      const header = value
        ? `<p><span style="font-weight: bold">Joint involvement</span> is ${ImprovementOptionsText[value]} as compared to the preceding visit. `
        : `<h3>Joint Involvement</h3>`;

      const sortedJointsData = sortUnifiedJointsByBilateralLeftRight(unifiedObjective.Joints || {});
      const jointsContent = getJointContent(sortedJointsData);

      content += header;
      content += jointsContent;
    }
  }

  // Myofascial Section
  if (shouldShowSection(unifiedObjective.Myofascial)) {
    const detail = unifiedObjective.Myofascial?.detail || {};
    const value = unifiedObjective.Myofascial?.value;
    const anyTouched = Object.values(detail).some((obj: any) => obj.touched);

    if (anyTouched || value) {
      const header = value
        ? `<p><span style="font-weight: bold">Myofascial involvement is</span> ${ImprovementOptionsText[value]} as compared to the preceding visit. `
        : `<h3>Myofascial Involvement</h3>`;

      // Get last visit myofascial data for unified comparison function
      const lastVisitMyofascial = visitForm.lastVisitUnifiedObjective?.Myofascial || {};

      const myofascialContent = getMyofascialContent(
        unifiedObjective.Myofascial || {},
        muscles?.data || [],
        lastVisitMyofascial,
        anyTouched
      );

      content += header;
      content += myofascialContent;
    }
  }

  // Orthopedic Section
  if (shouldShowSection(unifiedObjective.Orthopedic)) {
    const showOrtho = Object.values(unifiedObjective.Orthopedic?.detail || {}).find(
      (item: any) => item.touched
    ) as {
      touched: boolean;
      ID: number;
      side: string;
      testNotes: string;
      value: string;
    };

    const header = unifiedObjective.Orthopedic?.value
      ? `<p><span style="font-weight: bold;">Orthopedic evaluation findings</span> are ${
          ImprovementOptionsText[unifiedObjective.Orthopedic.value]
        } as compared to the preceding visit.</p>`
      : showOrtho
        ? `<h3>Orthopedic Evaluation Findings</h3>`
        : '';

    // Get last visit orthopedic data for unified comparison function
    const lastVisitOrthopedic = visitForm.lastVisitUnifiedObjective?.Orthopedic || {};

    const bodyDetail = getOrthopedicContent(
      unifiedObjective.Orthopedic || {},
      evals?.data || [],
      lastVisitOrthopedic
    );

    const orthopedicContent =
      bodyDetail !== '</ul></ul>'
        ? header + bodyDetail
        : unifiedObjective.Orthopedic?.value
          ? header
          : '';

    if (orthopedicContent) {
      content += orthopedicContent;
    }
  }

  // Neurological Section
  if (shouldShowSection(unifiedObjective.Neurological)) {
    const header = unifiedObjective.Neurological?.value
      ? `<p><span style="font-weight: bold;">Neurological evaluation findings</span> are ${
          ImprovementOptionsText[unifiedObjective.Neurological.value]
        } as compared to the preceding visit.</p>`
      : `<h3>Neurological Evaluation Findings</h3>`;

    const neurologicalContent = getNeurologicalContent(unifiedObjective.Neurological?.detail || {});

    content += header;
    content += neurologicalContent;
  }

  // Extra Notes Section
  if (unifiedObjective.extraNotes) {
    const escapedExtraNotes = escapeHtmlTags(unifiedObjective.extraNotes);
    content += `<h3>Notes</h3><p>${escapedExtraNotes}</p><br/>`; // Add spacing after Notes section
  }

  return content;
};
